import { type AutocompleteState, createAutocomplete } from "@algolia/autocomplete-core";
import { getAlgoliaResults } from "@algolia/autocomplete-preset-algolia";
import type { FacetFilters, Hit } from "algoliasearch";
import {
	type BaseSyntheticEvent,
	createContext,
	type Dispatch,
	type FC,
	type KeyboardEvent,
	type MouseEvent,
	type ReactNode,
	type RefObject,
	type SetStateAction,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import {useConfigContext} from "../../ConfigProvider.tsx";
import { useQuerySuggestions } from "../hooks/useQuerySuggestions.tsx";
import type { AlgoliaAttributes } from "../../types/AlgoliaAttributes";
import { createDebounceGate } from "../../utils/debounce.ts";

type AutocompleteCoreState = AutocompleteState<Hit<AlgoliaAttributes>>;

// Algolia bills per search request, so the autocomplete waits for a typing pause
// instead of querying on every keystroke.
export const SEARCH_DEBOUNCE_MS = 300;

interface AutocompleteContextProps {
	autocompleteState: AutocompleteCoreState;
	setAutocompleteState: Dispatch<SetStateAction<AutocompleteCoreState>>;
	setFilters: Dispatch<SetStateAction<string>>;
	setFacetFilters: Dispatch<SetStateAction<FacetFilters>>;
	autocomplete: ReturnType<
		typeof createAutocomplete<Hit<AlgoliaAttributes>, BaseSyntheticEvent, MouseEvent, KeyboardEvent>
	>;
	isModalOpen: boolean;
	setIsModalOpen: Dispatch<SetStateAction<boolean>>;
	inputRef: RefObject<HTMLInputElement | null>;
	formRef: RefObject<HTMLFormElement | null>;
	panelRef: RefObject<HTMLDivElement | null>;
	searchRef: RefObject<HTMLDivElement | null>;
	totalHits?: number;
	hits: Hit<AlgoliaAttributes>[];
	setHits: Dispatch<SetStateAction<Hit<AlgoliaAttributes>[]>>;
}

const AutocompleteContext = createContext<AutocompleteContextProps | undefined>(undefined);

export const AutocompleteProvider: FC<{ children: ReactNode }> = ({ children }) => {
	const searchRef = useRef<HTMLDivElement>(null);
	const inputRef = useRef<HTMLInputElement>(null);
	const formRef = useRef<HTMLFormElement>(null);
	const panelRef = useRef<HTMLDivElement>(null);
	const [filters, setFilters] = useState<string>("");
	const [facetFilters, setFacetFilters] = useState<FacetFilters>("");
	const [totalHits] = useState<number>();
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [hits, setHits] = useState<Hit<AlgoliaAttributes>[]>([]);

	const {
		tc,
		searchClient,
		algoliaConfig: { customizedUrl },
		indexPrefix,
	} = useConfigContext();

	const siteCode =
		typeof document !== "undefined"
			? document.body.dataset.site_code
			: undefined;
	const suffixSiteCode = siteCode === "aewi" ? "ÄWI" : "IFU";

	const [autocompleteState, setAutocompleteState] = useState<AutocompleteCoreState>({
		query: "",
		collections: [],
		isOpen: false,
		activeItemId: null,
		completion: null,
		context: {},
		status: "idle",
	});

	const querySuggestionsInCategory = useQuerySuggestions();
	// One gate per instance, stable across renders — see SEARCH_DEBOUNCE_MS.
	const waitForTypingPause = useMemo(() => createDebounceGate(SEARCH_DEBOUNCE_MS), []);
	const filtersRef = useRef(filters);
	const facetFiltersRef = useRef(facetFilters);

	useEffect(() => {
		filtersRef.current = filters;
		autocomplete.refresh().then();
	}, [filters]);

	useEffect(() => {
		facetFiltersRef.current = facetFilters;
		autocomplete.refresh().then();
	}, [facetFilters]);

	const autocomplete = useMemo(
		() =>
			createAutocomplete<
				Hit<AlgoliaAttributes>,
				BaseSyntheticEvent,
				MouseEvent,
				KeyboardEvent
			>({
				onSubmit({ state }) {
					window.location.href = `/${customizedUrl}/search/?input_search=${state.query}`;
				},
				onStateChange({ state }) {
					setAutocompleteState(state);
					const productCollection = state.collections?.find(
						(col) => col.source.sourceId === "products"
					);
					if (productCollection?.items?.length) {
						setHits(productCollection.items as Hit<AlgoliaAttributes>[]);
					} else {
						setHits([]);
					}
				},
				placeholder: tc.placeholder,
				openOnFocus: true,
				navigator: {
					navigate({ itemUrl }) {
						window.location.assign(itemUrl);
					},
					navigateNewTab({ itemUrl }) {
						const windowReference = window.open(itemUrl, '_blank', 'noopener');

						if (windowReference) {
							windowReference.focus();
						}
					},
					navigateNewWindow({ itemUrl }) {
						window.open(itemUrl, '_blank', 'noopener');
					},
				},
				async getSources({ query }) {
					if (!query) return [];
					// Resolves only once the user stops typing for SEARCH_DEBOUNCE_MS;
					// superseded keystrokes never get here and issue no request.
					await waitForTypingPause();
					return [
						{
							sourceId: "products",
							getItemUrl({ item }) {
								return item?.itemLink;
							},
							getItems() {
								const groupIndex = [
									indexPrefix,
									`${suffixSiteCode}_DEU_GROUPED`,
								]
									.filter((s) => s && s.length)
									.join("_");

								return getAlgoliaResults({
									searchClient,
									queries: [
										{
											indexName: groupIndex,
											params: {
												query,
												filters: filtersRef.current,
												facetFilters: facetFiltersRef.current,
												hitsPerPage: 1000,
												// Enables __autocomplete_queryID on hits, required for click analytics
												clickAnalytics: true,
											},
										},
									],
								});
							},
						},
						{
							sourceId: "information",
							getItemUrl({ item }) {
								return item?.itemLink;
							},
							getItems() {
								const infoIndex = [
									indexPrefix,
									`${suffixSiteCode}_DEU_content_crawler_pages`,
								]
									.filter((s) => s && s.length)
									.join("_");

								return getAlgoliaResults({
									searchClient,
									queries: [
										{
											indexName: infoIndex,
											params: {
												query,
												hitsPerPage: 5,
												attributesToSnippet: ["content:200"],
												snippetEllipsisText: "…",
												highlightPreTag: "<mark>",
												highlightPostTag: "</mark>",
											},
										},
									],
								});
							},
						},
						// eslint-disable-next-line @typescript-eslint/ban-ts-comment
						// @ts-expect-error
						...querySuggestionsInCategory.getSources({ state: autocompleteState }),
					];
				},
			}),
		[]
	);
	const { getEnvironmentProps } = autocomplete;

	useEffect(() => {
		if (!formRef.current || !panelRef.current || !inputRef.current) {
			return undefined;
		}
		const { onTouchStart, onTouchMove, onMouseDown } = getEnvironmentProps({
			formElement: formRef.current,
			inputElement: inputRef.current,
			panelElement: panelRef.current,
		});
		window.addEventListener("mousedown", onMouseDown);
		window.addEventListener("touchstart", onTouchStart);
		window.addEventListener("touchmove", onTouchMove);
		return () => {
			window.removeEventListener("mousedown", onMouseDown);
			window.removeEventListener("touchstart", onTouchStart);
			window.removeEventListener("touchmove", onTouchMove);
		};
	}, [getEnvironmentProps, autocompleteState.isOpen]);

	return (
		<AutocompleteContext.Provider
			value={{
				searchRef,
				panelRef,
				formRef,
				inputRef,
				autocompleteState,
				setAutocompleteState,
				autocomplete,
				setFilters,
				setFacetFilters,
				setIsModalOpen,
				isModalOpen,
				totalHits,
				hits,
				setHits,
			}}
		>
			{children}
		</AutocompleteContext.Provider>
	);
};

export const useAutocomplete = () => {
	const context = useContext(AutocompleteContext);
	if (!context) {
		throw new Error("useAutocomplete must be used within an AutocompleteProvider");
	}
	return context;
};
